import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import clsx from 'clsx';
import { useBoutiqueCalendarWeek } from './hooks/use-boutique-calendar-week';
import { useBoutiqueCurrentTime } from './hooks/use-boutique-current-time';
import { useBoutiqueEventPopover } from './hooks/use-boutique-event-popover';
import { buildBoutiqueDayEventLayoutList, buildBoutiqueHourList, formatBoutiqueHourLabel, getBoutiqueNowPosition, groupBoutiqueEventListByDate } from './boutique-calendar.utils';
import { BoutiqueCalendarEvent, BoutiqueCalendarLayoutEvent, BoutiqueCalendarVisibleRange } from './boutique-calendar.types';
import { BoutiqueCalendarAutoScrollDuration, BoutiqueCalendarAutoScrollOffset } from './boutique-calendar.constants';
import { animateScrollTo, scheduleFrame } from '@/utility/animate-scroll';
import BoutiqueCalendarToolbar from './BoutiqueCalendarToolbar';
import BoutiqueCalendarEventCard from './BoutiqueCalendarEventCard';
import BoutiqueCalendarEventPopover from './BoutiqueCalendarEventPopover';
import { ClassroomGroup } from '../../boutique-classroom.types';
import '@/assets/styles/views/boutique-classroom/boutique-calendar/boutique-calendar.styles.scss';

interface Props {
	eventList?: BoutiqueCalendarEvent[];
	groupList?: ClassroomGroup[];
	onEventJoin: (event: BoutiqueCalendarLayoutEvent) => void;
	onEventEdit?: (event: BoutiqueCalendarLayoutEvent) => void;
	onVisibleRangeChange?: (range: BoutiqueCalendarVisibleRange) => void;
	isLoading?: boolean;
	className?: string;
}

const EMPTY_EVENT_LIST: BoutiqueCalendarEvent[] = [];
const EMPTY_GROUP_LIST: ClassroomGroup[] = [];

export const BoutiqueCalendar: React.FC<Props> = memo(({ eventList = EMPTY_EVENT_LIST, groupList = EMPTY_GROUP_LIST, onEventJoin, onEventEdit, onVisibleRangeChange, isLoading = false, className }) => {
	const now = useBoutiqueCurrentTime();
	const { dayList, rangeLabel, onPreviousWeek, onNextWeek, onCurrentWeek } = useBoutiqueCalendarWeek(now);

	const { popoverState, onOpenPopover, onClosePopover } = useBoutiqueEventPopover();

	const handleEditEvent = useCallback(
		(event: BoutiqueCalendarLayoutEvent) => {
			onClosePopover();
			onEventEdit?.(event);
		},
		[onClosePopover, onEventEdit]
	);

	const visibleRange = useMemo<BoutiqueCalendarVisibleRange | null>(() => (dayList.length ? { from: dayList[0].key, to: dayList[dayList.length - 1].key } : null), [dayList]);

	useEffect(() => {
		if (visibleRange) onVisibleRangeChange?.(visibleRange);
	}, [onVisibleRangeChange, visibleRange]);

	const hourList = useMemo(() => buildBoutiqueHourList(eventList), [eventList]);

	const eventMap = useMemo(() => groupBoutiqueEventListByDate(eventList), [eventList]);

	const dayColumnList = useMemo(() => dayList.map(weekDay => ({ ...weekDay, eventList: buildBoutiqueDayEventLayoutList(eventMap[weekDay.key] ?? [], hourList[0]) })), [dayList, eventMap, hourList]);

	const nowPosition = useMemo(() => (dayList.some(weekDay => weekDay.isToday) ? getBoutiqueNowPosition(hourList, now) : null), [dayList, hourList, now]);

	const scrollRef = useRef<HTMLDivElement>(null);
	const gridRef = useRef<HTMLDivElement>(null);
	const bodyRef = useRef<HTMLDivElement>(null);
	const headGutterRef = useRef<HTMLSpanElement>(null);
	const headDayRefs = useRef<Array<HTMLDivElement | null>>([]);
	const lastAutoScrolledRangeRef = useRef<string | null>(null);
	const cancelAutoScrollRef = useRef<(() => void) | null>(null);
	const cancelAutoScrollXRef = useRef<(() => void) | null>(null);
	useEffect(
		() => () => {
			cancelAutoScrollRef.current?.();
			cancelAutoScrollXRef.current?.();
		},
		[]
	);
	useEffect(() => {
		const rangeKey = visibleRange?.from ?? null;
		if (isLoading) {
			lastAutoScrolledRangeRef.current = null;
			return;
		}
		if (!rangeKey || !hourList.length || lastAutoScrolledRangeRef.current === rangeKey) return;

		let targetRatio: number | null = nowPosition?.topRatio ?? null;
		let targetDayIndex: number | null = nowPosition ? dayList.findIndex(weekDay => weekDay.isToday) : null;
		if (targetRatio === null) {
			let earliestEvent: BoutiqueCalendarLayoutEvent | null = null;
			dayColumnList.forEach((dayColumn, dayIndex) => {
				dayColumn.eventList.forEach(dayEvent => {
					if (earliestEvent === null || dayEvent.topRatio < earliestEvent.topRatio) {
						earliestEvent = dayEvent;
						targetDayIndex = dayIndex;
					}
				});
			});
			targetRatio = (earliestEvent as BoutiqueCalendarLayoutEvent | null)?.topRatio ?? null;
		}

		const cancelFrame = scheduleFrame(() => {
			const scrollElement = scrollRef.current;
			const gridElement = gridRef.current;
			const bodyElement = bodyRef.current;
			if (!scrollElement || !gridElement || !bodyElement) return;
			const hourHeight = bodyElement.offsetHeight / hourList.length;
			if (!hourHeight) return;
			lastAutoScrolledRangeRef.current = rangeKey;
			const verticalScroller = scrollElement.scrollHeight > scrollElement.clientHeight ? scrollElement : gridElement;
			const top = targetRatio === null ? 0 : Math.max(0, targetRatio * hourHeight - BoutiqueCalendarAutoScrollOffset);
			cancelAutoScrollRef.current?.();
			cancelAutoScrollRef.current = animateScrollTo(verticalScroller, top, BoutiqueCalendarAutoScrollDuration);

			const dayElement = targetDayIndex === null ? null : headDayRefs.current[targetDayIndex];
			if (gridElement.scrollWidth <= gridElement.clientWidth) return;
			const left = dayElement ? Math.max(0, dayElement.offsetLeft - (headGutterRef.current?.offsetWidth ?? 0)) : 0;
			cancelAutoScrollXRef.current?.();
			cancelAutoScrollXRef.current = animateScrollTo(gridElement, left, BoutiqueCalendarAutoScrollDuration, 'x');
		});
		return cancelFrame;
	}, [dayColumnList, dayList, hourList.length, isLoading, nowPosition, visibleRange?.from]);

	return (
		<section className={clsx('boutique-calendar', className)}>
			<BoutiqueCalendarToolbar
				rangeLabel={rangeLabel}
				onPreviousWeek={onPreviousWeek}
				onNextWeek={onNextWeek}
				onCurrentWeek={onCurrentWeek}
			/>

			<div
				ref={gridRef}
				className="boutique-calendar-grid">
				{isLoading && (
					<div
						className="boutique-calendar-loading"
						aria-busy="true">
						<span className="boutique-calendar-loading-ring" />
					</div>
				)}
				<div className="boutique-calendar-head">
					<span
						ref={headGutterRef}
						className="boutique-calendar-head-gutter"
					/>
					{dayList.map((headDay, dayIndex) => (
						<div
							key={headDay.key}
							ref={element => {
								headDayRefs.current[dayIndex] = element;
							}}
							className={clsx('boutique-calendar-head-day', { today: headDay.isToday })}>
							<span className="boutique-calendar-head-day-number">{headDay.dayNumber}</span>
							<span className="boutique-calendar-head-day-name">{headDay.dayName}</span>
						</div>
					))}
				</div>

				<div
					ref={scrollRef}
					className="custom-scroll boutique-calendar-scroll">
					<div
						ref={bodyRef}
						className="boutique-calendar-body"
						style={{ '--boutique-calendar-hour-count': hourList.length } as React.CSSProperties}>
						<div className="boutique-calendar-time-column">
							{hourList.map(timeHour => (
								<div
									key={timeHour}
									className="boutique-calendar-time-slot">
									<span className="boutique-calendar-time-label">{formatBoutiqueHourLabel(timeHour)}</span>
								</div>
							))}
							{nowPosition && (
								<div
									className="boutique-calendar-now"
									style={{ '--boutique-now-top': nowPosition.topRatio } as React.CSSProperties}>
									<span className="boutique-calendar-now-label">{nowPosition.label}</span>
									<span className="boutique-calendar-now-dot" />
								</div>
							)}
						</div>

						{dayColumnList.map(dayColumn => (
							<div
								key={dayColumn.key}
								className={clsx('boutique-calendar-day-column', { today: dayColumn.isToday })}>
								{dayColumn.eventList.map(dayEvent => (
									<BoutiqueCalendarEventCard
										key={dayEvent.id}
										event={dayEvent}
										isActive={popoverState?.event?.id === dayEvent.id}
										onClick={onOpenPopover}
									/>
								))}
							</div>
						))}
					</div>
				</div>
			</div>

			{popoverState && (
				<BoutiqueCalendarEventPopover
					event={popoverState.event}
					groupList={groupList}
					anchorElement={popoverState.anchorElement}
					onClose={onClosePopover}
					onJoin={onEventJoin}
					onEdit={onEventEdit ? handleEditEvent : undefined}
				/>
			)}
		</section>
	);
});

BoutiqueCalendar.displayName = 'BoutiqueCalendar';

export default BoutiqueCalendar;
